import { useState, useEffect, useCallback, useRef } from 'react';
import supabase from '../supabase/supabase';

export const useFanProfileData = (userId) => {
  const [unlockedDrops, setUnlockedDrops] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [accessGrants,  setAccessGrants]  = useState([]);
  const [loading,       setLoading]       = useState(true);
  const channelsRef = useRef([]);

  const fetchUnlockedDrops = useCallback(async () => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        .from('d2f_drop_unlocks')
        .select(`
          id,
          amount_paid,
          created_at,
          drop:d2f_drops(
            id, title, cover_url, genre, tags,
            min_price, suggested_price, artist_id
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (!error) setUnlockedDrops(data || []);
    } catch {
      setUnlockedDrops([]);
    }
  }, [userId]);

  const fetchSubscriptions = useCallback(async () => {
    if (!userId) return;
    try {
      // Use explicit FK hints to avoid ambiguous column errors.
      // avatar_url is now present after the 20240535 migration.
      const { data, error } = await supabase
        .from('d2f_subscriptions')
        .select(`
          id,
          status,
          amount_paid,
          started_at,
          expires_at,
          tier:d2f_subscription_tiers!tier_id(id, name, color),
          artist:d2f_artists!artist_id(id, name, avatar_url, genre)
        `)
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('started_at', { ascending: false });
      if (!error) setSubscriptions(data || []);
    } catch {
      setSubscriptions([]);
    }
  }, [userId]);

  const fetchAccessGrants = useCallback(async () => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        .from('d2f_access_grants')
        .select('id, content_id, artist_id, amount_paid, created_at')
        .eq('user_id', userId);
      if (!error) setAccessGrants(data || []);
    } catch {
      setAccessGrants([]);
    }
  }, [userId]);

  const fetchAll = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    try {
      await Promise.all([
        fetchUnlockedDrops(),
        fetchSubscriptions(),
        fetchAccessGrants(),
      ]);
    } finally {
      setLoading(false);
    }
  }, [userId, fetchUnlockedDrops, fetchSubscriptions, fetchAccessGrants]);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }

    fetchAll();

    // Clean up any stale channels before creating new ones
    channelsRef.current.forEach(ch => supabase.removeChannel(ch));
    channelsRef.current = [];

    // Build the full listener chain BEFORE calling .subscribe()
    const ts = Date.now();

    const unlockCh = supabase
      .channel(`fan-unlocks-${userId}-${ts}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'd2f_drop_unlocks',
        filter: `user_id=eq.${userId}`,
      }, () => fetchUnlockedDrops())
      .subscribe();

    const subCh = supabase
      .channel(`fan-subs-${userId}-${ts}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'd2f_subscriptions',
        filter: `user_id=eq.${userId}`,
      }, () => fetchSubscriptions())
      .subscribe();

    channelsRef.current = [unlockCh, subCh];

    return () => {
      channelsRef.current.forEach(ch => supabase.removeChannel(ch));
      channelsRef.current = [];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const stats = {
    unlocked:  unlockedDrops.length,
    following: subscriptions.length,
    grants:    accessGrants.length,
    library:   unlockedDrops.length + accessGrants.length,
  };

  return { unlockedDrops, subscriptions, accessGrants, stats, loading, refetch: fetchAll };
};